import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { StateService } from '@uirouter/core';

import { VideogularComponent } from 'vbrick-player-src/videogular/Videogular.Component';
import { VgStates } from '@vbrick/vbrick-player/app/player/videogular/VgStates';

import { AccountLicenseService } from 'rev-shared/security/AccountLicense.Service';
import { AnalyticsViewContext } from 'rev-shared/analytics/Constant';
import { AppBootstrapService } from 'rev-shared/bootstrap/AppBootstrap.Service';
import { ApprovalStatus } from 'rev-shared/media/MediaConstants';
import { ConfirmationDialogAngularComponent } from 'rev-shared/ui/dialog/ConfirmationDialogAngular.Component';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { EmbedPreviewService } from 'rev-shared/embedPreview/EmbedPreview.Service';
import { IModernPlayback } from 'rev-shared/videoPlayer/IModernPlayback';
import { ISidebarConfig } from 'rev-shared/ui/sidebarLayoutAngular/ISidebarConfig';
import { ISidebarToggleEvent } from 'rev-shared/ui/sidebarLayoutAngular/VbUiSidebar.Component';
import { ITimeMarker } from 'vbrick-player-src/videogular/controls/VbTimeMarkers.Component';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { VbUiSidebarLayoutComponent } from 'rev-shared/ui/sidebarLayoutAngular/VbUiSidebarLayout.Component';
import { VideoSource } from 'rev-shared/media/VideoSource';
import { VideoSourceType } from 'rev-shared/media/VideoSourceType';
import { VideoStatus } from 'rev-shared/media/VideoStatus';
import { isMobile } from 'rev-shared/util/UserAgentUtil';
import { isNumber } from 'rev-shared/util';
import { refreshViewerIdOverlay } from 'rev-shared/videoPlayer/VideoOverlayUtility';
import { PORTAL_MEDIA_STATE } from 'rev-shared/bootstrap/AppStateNames';

import { MainHeaderPositionService } from 'rev-portal/navigation/mainHeader/MainHeaderPosition.Service';
import { SUPPORTED_VIDEO_STATUSES } from 'rev-portal/media/recording/RecordingProcess.Component';

import { IVideoPlaybackConfig } from './IVideoPlaybackConfig';
import { IVideoPlaybackParams } from './IVideoPlaybackParameters';
import { VideoAiAssistantService } from './videoAssistant/VideoAiAssistant.Service';
import { VideoPlaybackService } from './VideoPlayback.Service';
import { VideoPlaybackSidebarButton } from './VideoPlaybackSidebarModel';
import { VideoPulseService } from './pulse/VideoPulse.Service';
import { VideoReviewService } from './videoReview/VideoReview.Service';
import { VideoUserTagsService } from './inThisVideo/VideoUserTags.Service';

import menuItemStyles from 'rev-shared/ui/menu-item/menu-item.module.less';
import styles from './VideoPlayback.module.less';
import 'rev-less/video/video-main.less';

const STATE_REDIRECT_AFTER_DELETE = PORTAL_MEDIA_STATE;

const VIDEO_PLAYBACK_CSS_VARIABLE = {
	sidebarPanelWidth: '--sidebar-panel-width'
};


@Component({
	providers: [VideoAiAssistantService],
	selector: 'video-playback',
	templateUrl: './VideoPlayback.Component.html',
	host: { 'class': 'theme-primary box-block' }
})
export class VideoPlaybackComponent implements OnDestroy, OnInit {
	@Input('config') public configBinding: IVideoPlaybackConfig;
	@Input() public previousState: { name: string; params: any; };
	@Input() public hasMediaEditAuth: boolean;
	@Input() public mediaFeatures: any;
	@ViewChild('sidebarLayout') public sidebarLayoutComponent: VbUiSidebarLayoutComponent;

	public readonly styles = styles;
	public readonly menuItemStyles = menuItemStyles;
	public readonly VideoPlaybackSidebarButton = VideoPlaybackSidebarButton;

	public config: IVideoPlaybackConfig;
	public isDeleting: boolean;
	public isPrepositionOpen: boolean;
	public isReplaceVideoOpen: boolean;
	public sidebarFilter: string;
	public vgAPI: VideogularComponent;
	public video: any;
	public activeSidebarButtonId: string;
	public isReportsOpen: boolean;
	public isUserAuthenticated: boolean;
	public markers: ITimeMarker[];
	public params: IVideoPlaybackParams;
	public playableStatus: VideoStatus;
	public playlist: any;
	public playlistId: string;
	public sidebarConfig: ISidebarConfig;
	public videoId: string;
	public isEmbedPreviewEnabled: boolean;
	public analyticsViewContext: AnalyticsViewContext = AnalyticsViewContext.Portal;

	private prevPlayerSize: {
		height: number,
		width: number
	};
	private isMobile: boolean;
	private preservedVolume: number;
	private rxSubscriptions: Subscription[];
	private windowUpdateSizeHandler: () => void;

	constructor(
		private readonly $state: StateService,
		private readonly Dialog: DialogService,
		private readonly MainHeaderPosition: MainHeaderPositionService,
		private readonly SecurityContext: SecurityContextService,
		private readonly TranslateService: TranslateService,
		private readonly UserContext: UserContextService,
		private readonly VideoPlaybackSvc: VideoPlaybackService,
		private readonly VideoPulse: VideoPulseService,
		private readonly VideoUserTags: VideoUserTagsService,
		private readonly VideoReviewService: VideoReviewService,
		private readonly accountLicense: AccountLicenseService,
		private readonly videoPlaybackElementRef: ElementRef,
		public readonly VideoAiAssistantService: VideoAiAssistantService,
		public readonly AppBootstrapService: AppBootstrapService,
		public readonly EmbedPreviewService: EmbedPreviewService
	) { }

	public ngOnInit(): void {
		this.isMobile = isMobile();
		this.isUserAuthenticated = this.UserContext.isUserAuthenticated();
		this.VideoPulse.setActiveSubtitleLanguage(null);

		const appCfg = this.AppBootstrapService.appConfig?.videoPlayerConfig;
		this.config = !appCfg ? this.configBinding : {
			...appCfg,
			...this.configBinding
		};

		const markers$: Observable<ITimeMarker[]> = combineLatest([
			this.VideoPulse.taggedUserRangeMarkers$,
			this.VideoPulse.transcriptionMarkers$,
			this.VideoUserTags.activeUserTagMarkers$,
			this.VideoReviewService.reviewMarkers$
		]).pipe(
			map(markerSources => markerSources.flat())
		);

		this.rxSubscriptions = [
			markers$.subscribe(data => this.markers = data),
			this.VideoPlaybackSvc.playlist$.subscribe(data => this.playlist = data),
			this.VideoPlaybackSvc.playlistId$.subscribe(data => this.playlistId = data),
			this.VideoPlaybackSvc.video$.subscribe(video => this.onVideoChange(video)),
			this.VideoPlaybackSvc.videoId$.subscribe(videoId => this.onVideoIdChange(videoId)),
			this.VideoPlaybackSvc.videoPlayableStatus$.subscribe(data => this.playableStatus = data),
			this.VideoPlaybackSvc.videoPlayerParams$.subscribe(data => this.onVideoPlayerParamsChanged(data)),
			this.EmbedPreviewService.data$.subscribe(d => this.isEmbedPreviewEnabled = d.isPreviewEnabled)
		];

		// reevaluatation of the height on mobile
		if (this.isMobile) {
			window.addEventListener('orientationchange', this.windowUpdateSizeHandler);
			window.addEventListener('resize', this.windowUpdateSizeHandler);
			window.document.addEventListener('touchend', this.windowUpdateSizeHandler);
		}

		this.managePlaybackConfig();
	}

	public ngOnDestroy(): void {
		this.isReportsOpen = false;
		this.rxSubscriptions.forEach(subscription => subscription.unsubscribe());
		this.EmbedPreviewService.clearData();
		this.VideoAiAssistantService.clearSessionData();

		if (this.isMobile) {
			window.removeEventListener('orientationchange', this.windowUpdateSizeHandler);
			window.removeEventListener('resize', this.windowUpdateSizeHandler);
			window.document.removeEventListener('touchend', this.windowUpdateSizeHandler);
		}
	}

	public get canEditVideo(): boolean {
		return this.video?.canEditVideo && !this.video.legalHold;
	}

	public get isMuteBtnOnLeft(): boolean {
		return this.video.source === VideoSource.SCHEDULED_EVENT
			&& this.video.sourceType === VideoSourceType.WEBEX_LIVE_STREAM;
	}

	public get isReadyToPlay(): boolean {
		return this.playableStatus === VideoStatus.READY;
	}

	public get playbackCurrentTime(): number {
		return this.vgAPI ? this.vgAPI.currentTime : 0;
	}

	public updatePlayerSeekTime(time: number): void {
		this.vgAPI.seekTime(time / 1000);
	}

	public goToPreviousState(): void {
		if (this.previousState) {
			this.$state.transitionTo(this.previousState.name, this.previousState.params);
		}
	}

	public deleteVideo(): void {
		this.openDialog(
			'Media_Videos_Delete_Confirmation',
			'Media_Videos_Delete_Title'
		).result.then(() => {
			this.isDeleting = true;
			return this.VideoPlaybackSvc.deleteVideo(this.videoId);
		}).then(() => {
			this.$state.go(STATE_REDIRECT_AFTER_DELETE);
		}, err => {
			this.isDeleting = false;
			if (err) {
				throw new Error(`Error deleting the video -> ${err}`);
			}
		});
	}

	public get height(): string {
		if (this.isReportsOpen) {
			return 'auto';
		}

		// mobile height is not reliably conveyed by 100vh
		if (this.isMobile) {
			const mainHeaderHeight: number = this.MainHeaderPosition.height || 0;
			const calculatedHeight: number = window.innerHeight - mainHeaderHeight;

			return calculatedHeight + 'px';
		}

		return this.MainHeaderPosition.height ? `calc(100vh - ${this.MainHeaderPosition.height}px)` : '100vh';
	}

	public get isVideoOwner(): boolean {
		return [this.video.replacedBy, this.video.owner.userId, this.video.approval.userId]
			.includes(this.UserContext.getUser().id);
	}

	public get isLoadingInitialVideo(): boolean {
		return !this.video;
	}

	public get isLoadingNewVideo(): boolean {
		return !this.isLoadingInitialVideo && this.videoId !== this.video.id;
	}

	private get mediaViewingAllowed(): boolean {
		return this.accountLicense.mediaViewingAllowed;
	}

	public get isOverlayDialogOpen(): boolean {
		return this.isPrepositionOpen ||
			this.isReplaceVideoOpen;
	}

	public get isRecordingStatusSurfaced(): boolean {
		return !this.isReadyToPlay &&
			this.video.videoConference &&
			this.mediaViewingAllowed &&
			!this.video.isReplacing &&
			SUPPORTED_VIDEO_STATUSES.includes(this.video.status);
	}

	public onPlayerSizeChanged(playerSize: { height: number, width: number }): void {
		refreshViewerIdOverlay(this.video, this.prevPlayerSize, playerSize);
		this.prevPlayerSize = playerSize;
	}

	public onClearConversationClick(): void {
		this.openDialog(
			'ClearConversation_Confirmation',
			'ClearConversation'
		).result.then(() => {
			this.VideoAiAssistantService.clearConversation(this.video.id);
		});
	}

	private applyLastPlaybackQualitySelection(newVideo: any): any {
		if (!this.video || !this.vgAPI || !newVideo || !newVideo.playbacks) {
			return newVideo;
		}

		const lastActiveSource: any = this.vgAPI.activeSource;
		const isSelectedQValue: boolean = lastActiveSource && !!lastActiveSource.qValue;

		if (isSelectedQValue && newVideo.playbacks.length > 1) {
			const newPlaybackMatch: IModernPlayback = newVideo.playbacks.find(option => option.qValue === lastActiveSource.qValue);

			if (newPlaybackMatch) {
				newPlaybackMatch.isDefault = true;
			}
		}

		return newVideo;
	}

	private getDefaultSidebarClickedButtonId(): VideoPlaybackSidebarButton {
		if (this.isMobile || !this.config?.showSidebar) {
			return;
		}

		if (this.config.initialSidebarButton) {
			return this.config.initialSidebarButton as any;
		} else if (this.video.chapterInfo.hasChapters && !this.config?.sidebarDisplay?.hideChapters && !this.config?.isEmbedFullPlayer) {
			return VideoPlaybackSidebarButton.CHAPTERS;
		} else if (this.isReviewSidebarVisible && !this.config?.sidebarDisplay?.hideReview && !this.config?.isEmbedFullPlayer) {
			return VideoPlaybackSidebarButton.REVIEW;
		}

		return undefined;
	}

	private getVideoPlaybackSidebarModel(): ISidebarConfig {
		return {
			buttons: [
				{
					id: VideoPlaybackSidebarButton.INFO,
					iconClass: 'vb-icon vb-icon-center vb-icon-info',
					label: this.TranslateService.instant('Media_VideoInformation'),
					visible: !this.config?.sidebarDisplay?.hideInfo
				},
				{
					id: VideoPlaybackSidebarButton.COMMENTS,
					iconClass: 'vb-icon vb-icon-center vb-icon-chat',
					label: this.TranslateService.instant('Media_NumVideoComments', { '0': this.video.commentCount }),
					visible: this.isUserAuthenticated && this.mediaFeatures.enableComments && this.video.commentsEnabled && !this.config?.sidebarDisplay?.hideComments
				},
				{
					id: VideoPlaybackSidebarButton.SHARE,
					iconClass: 'vb-icon vb-icon-center vb-icon-share-alt',
					label: this.TranslateService.instant('Media_Sharing'),
					visible: !this.config?.isEmbedFullPlayer
				},
				{
					id: VideoPlaybackSidebarButton.PULSE,
					iconClass: 'vb-icon vb-icon-center vb-icon-details',
					label: this.TranslateService.instant('Media_Videos_Transcript'),
					visible: !this.video.isLive && !this.config?.sidebarDisplay?.hidePulse
				},
				{
					id: VideoPlaybackSidebarButton.REVIEW,
					iconClass: 'vb-icon vb-icon-center vb-icon-alert',
					label: this.TranslateService.instant('Review'),
					visible: this.isReviewSidebarVisible && !this.config?.sidebarDisplay?.hideReview
				},
				{
					id: VideoPlaybackSidebarButton.AI,
					iconClass: 'vb-icon vb-icon-center vb-icon-sparkle',
					label: this.TranslateService.instant('Media_Videos_Assistant'),
					visible: this.video.transcriptionFiles.some(t => t.languageId === 'en') && this.config.showAiAssistant && !this.config?.sidebarDisplay?.hideVideoAssistant
				},
				{
					id: VideoPlaybackSidebarButton.PLAYLIST,
					iconClass: 'vb-icon vb-icon-center vb-icon-playlist',
					label: this.TranslateService.instant('Media_Videos_Playlists_AddTo'),
					visible: this.isUserAuthenticated && !this.video.legalHold && !this.config?.sidebarDisplay?.hidePlaylist
				},
				{
					id: VideoPlaybackSidebarButton.CHAPTERS,
					iconClass: 'vb-icon vb-icon-center vb-icon-chapters',
					label: this.TranslateService.instant('Media_Videos_Chapters'),
					visible: this.video.chapterInfo?.hasChapters && !this.config?.sidebarDisplay?.hideChapters
				},
				{
					id: VideoPlaybackSidebarButton.REPORTS,
					iconClass: 'vb-icon vb-icon-center vb-icon-graph',
					label: this.TranslateService.instant('Reports'),
					onClick: () => this.openReports(),
					visible: this.isUserAuthenticated && this.video.reportsEnabled && !this.config?.sidebarDisplay?.hideAnalytics
				}
			],
			defaultClickedButtonId: this.getDefaultSidebarClickedButtonId()
		};
	}

	public loadPlaylistVideo(videoId: string): void {
		this.$state.go('.', { videoId });
	}

	public onPrepositionDialogClose(): void {
		this.isPrepositionOpen = false;
	}

	public onReplaceVideoDialogClose(): void {
		this.isReplaceVideoOpen = false;
	}

	public onPlay(): void {
		this.VideoPlaybackSvc.playerTouched();
	}

	public onPlaybackEnded(): void {
		if (this.playlist) {
			this.playNextVideoInPlaylist();
		}

		if (this.config.playInLoop) {
			window.setTimeout(() => this.vgAPI.play(), 500);
			return;
		}
	}

	public onPlayerReady(vgAPI: VideogularComponent): void {
		this.vgAPI = vgAPI;

		if (isNumber(this.preservedVolume)) {
			vgAPI.setVolume(this.preservedVolume);
		}

		if (this.video?.chapterInfo) {
			this.rxSubscriptions.push(
				vgAPI.currentTime$.subscribe(currentTime => {
					this.video?.chapterInfo.updatePlaybackTime(currentTime);
				})
			);
		}
	}

	public onSidebarToggle(event: ISidebarToggleEvent): void {
		this.activeSidebarButtonId = event.activeSidebarButtonId;

		if (this.activeSidebarButtonId && this.activeSidebarButtonId !== VideoPlaybackSidebarButton.PULSE) {
			this.sidebarFilter = '';
		}
	}

	public onCloseSidebar(): void {
		this.sidebarLayoutComponent?.closeSidebar();
	}

	private onVideoChange(newVideo: any): void {
		this.video = this.applyLastPlaybackQualitySelection(newVideo);
		if (this.video) {
			this.sidebarConfig = this.getVideoPlaybackSidebarModel();
		}
	}

	private onVideoIdChange(newVideoId: string): void {
		this.preserveVolume();

		this.videoId = newVideoId;
		this.isReportsOpen = false;
	}

	public onVideoPlaybackReportsClose(): void {
		this.isReportsOpen = false;
	}

	private get isReviewSidebarVisible(): boolean {
		const isPendingApproval = this.video.approval.status === ApprovalStatus.PENDING_APPROVAL;
		const isRejected = this.video.approval.status === ApprovalStatus.REJECTED;
		const hasPermission = this.video.approval.isStepApprover || this.isVideoOwner || this.isMediaAdmin;

		return (isPendingApproval && hasPermission) || (isRejected && this.isVideoOwner);
	}

	private get isMediaAdmin(): boolean {
		return !!this.SecurityContext.checkAuthorization('admin.media');
	}

	private onVideoPlayerParamsChanged(params: IVideoPlaybackParams) {
		const oldParsedStartAt: number = this.params ? this.params.parsedStartAt : null;

		this.params = params;

		// seek, if startAt has changed
		if (isNumber(params.parsedStartAt) && params.parsedStartAt !== oldParsedStartAt && this.vgAPI) {
			this.vgAPI.seekTime(params.parsedStartAt / 1000); // ms to sec
		}
	}

	private openReports(): void {
		this.isReportsOpen = true;
	}

	private playNextVideoInPlaylist(): void {
		const currentIndex: number = this.playlist.videos.findIndex(video => video.id === this.video.id);
		const nextIndex: number = currentIndex === this.playlist.videos.length - 1 ?
			0 :
			currentIndex + 1;

		if (currentIndex === nextIndex) {
			//giving timing to finish previous complete
			//before play again...
			window.setTimeout(() => this.playVideo(), 500);
			return;
		}

		this.loadPlaylistVideo(this.playlist.videos[nextIndex].id);
	}

	private playVideo(): void {
		if (this.vgAPI?.play && this.vgAPI.currentState !== VgStates.PLAY) {
			this.vgAPI.play();
		}
	}

	private preserveVolume(): void {
		if (this.vgAPI && isNumber(this.vgAPI.volume)) {
			this.preservedVolume = this.vgAPI.volume;
		}
	}

	private openDialog(messageKey: string, titleKey: string, actionKey: string = 'Ok', cancelKey: string = 'Cancel'): ConfirmationDialogAngularComponent {
		return this.Dialog.openConfirmationDialog(
			{
				message: this.TranslateService.instant(messageKey),
				title: this.TranslateService.instant(titleKey),
				actionText: this.TranslateService.instant(actionKey),
				cancelText: this.TranslateService.instant(cancelKey)
			}
		);
	}

	private managePlaybackConfig(): void {
		if (this.config.sidebarPanelWidthInPx) {
			this.videoPlaybackElementRef.nativeElement.style.setProperty(VIDEO_PLAYBACK_CSS_VARIABLE.sidebarPanelWidth, this.config.sidebarPanelWidthInPx);
		}
		this.sidebarFilter = this.config.sidebarFilter;
	}
}
